//
// Copyright 2022 DXOS.org
//

import * as Atom from 'effect/reactivity/Atom';

import { type CleanupFn, Event } from '@dxos/async';
import { Context } from '@dxos/context';
import { StackTrace } from '@dxos/debug';
import { type Entity, Query, QueryAST, type QueryResult } from '@dxos/echo';
import { type AggregateValue, GroupBy } from '@dxos/echo-host/query';
import { invariant } from '@dxos/invariant';
import { log } from '@dxos/log';
import { trace } from '@dxos/tracing';
import { countWork, getDeep, isNonNullable } from '@dxos/util';

import { getObjectCore, isEchoObject } from '../echo-handler/index.ts';
import { type QueryContext, type SourceEntry } from './query-context.ts';
import { queryMetrics } from './query-metrics.ts';

/**
 * True when any part of the query asks for deleted entities.
 *
 * `.options({ deleted })` produces a node that scoping wraps further, so the flag can sit at any
 * depth rather than on the root.
 */
const _queryIncludesDeleted = (query: QueryAST.Query): boolean => {
  let includesDeleted = false;
  QueryAST.visit(query, (node) => {
    if (node.type === 'options' && (node.options.deleted === 'include' || node.options.deleted === 'only')) {
      includesDeleted = true;
    }
  });
  return includesDeleted;
};

/** A query's results in their public shape. */
type PresentedResults<T extends Entity.Unknown> = {
  kind: 'entities' | 'groups' | 'records';
  objects: T[];
  entries: QueryResult.EntityEntry<T>[];
};

/**
 * Predicate based query.
 */
export class QueryResultImpl<T extends Entity.Unknown = Entity.Unknown> implements QueryResult.QueryResult<T> {
  private readonly _event = new Event<QueryResult.QueryResult<T>>();
  private readonly _diagnostic: QueryDiagnostic;
  /** Grouping key under which this query's metrics are recorded. */
  private readonly _metricsKey: string;

  private _isActive = false;
  private _resultCache?: QueryResult.EntityEntry<T>[] = undefined;
  private _objectCache?: T[] = undefined;
  private _subscribers: number = 0;
  private _atom: Atom.Atom<T[]> | undefined = undefined;
  /** When the reactive query started, until it first holds every source's answer. */
  private _startedAt?: number = undefined;

  constructor(
    private readonly _queryContext: QueryContext<T>,
    private readonly _query: Query.Query<T>,
  ) {
    // Assigned before the context subscription below, whose recompute records under it.
    this._metricsKey = Query.pretty(this._query);
    queryMetrics.created(this._metricsKey);

    this._queryContext.changed.on(() => {
      if (this._recomputeResult()) {
        countWork('echo.querySubscriberCallbacks', this._event.listenerCount());
        this._event.emit(this);
      }
    });

    this._queryContext.update(this._query.ast);

    this._diagnostic = {
      isActive: this._isActive,
      filter: JSON.stringify(this._query),
      creationStack: new StackTrace(),
    };
    registerQueryDiagnostic(this._diagnostic);

    log('construct', { query: Query.pretty(this._query) });
  }

  get query(): Query.Query<T> {
    return this._query;
  }

  get entries(): QueryResult.EntityEntry<T>[] {
    this._checkQueryIsRunning();
    this._ensureCachePresent();
    return this._resultCache!;
  }

  get results(): T[] {
    this._checkQueryIsRunning();
    this._ensureCachePresent();
    return this._objectCache!;
  }

  /**
   * Execute the query once and return the results.
   * Does not subscribe to updates.
   */
  async run(opts?: { timeout?: number }): Promise<T[]> {
    return (await this._runOnce(opts)).objects;
  }

  /**
   * Execute the query once and return the entries with match metadata.
   * Does not subscribe to updates.
   */
  async runEntries(opts?: { timeout?: number }): Promise<QueryResult.EntityEntry<T>[]> {
    return (await this._runOnce(opts)).entries;
  }

  async first(opts?: { timeout?: number }): Promise<T> {
    const objects = await this.run(opts);
    if (objects.length === 0) {
      throw new Error('No objects found');
    }

    return objects[0];
  }

  async firstOrUndefined(opts?: { timeout?: number }): Promise<T | undefined> {
    const objects = await this.run(opts);
    return objects.at(0);
  }

  /**
   * Runs the query synchronously and returns all results.
   * WARNING: This method will only return the data already cached and may return incomplete results.
   * Use `this.run()` for a complete list of results stored on-disk.
   */
  runSync(): T[] {
    this._ensureCachePresent();
    return this._objectCache!;
  }

  /**
   * Runs the query synchronously and returns all entries with match metadata.
   * WARNING: This method will only return the data already cached and may return incomplete results.
   * Use `this.runEntries()` for a complete list of entries stored on-disk.
   */
  runSyncEntries(): QueryResult.EntityEntry<T>[] {
    this._ensureCachePresent();
    return this._resultCache!;
  }

  /**
   * Subscribe to query results.
   * Updates only when the identity or the order of the objects changes.
   * Does not update when the object properties change.
   */
  // TODO(burdon): Change to SubscriptionHandle (make uniform).
  subscribe(callback?: (query: QueryResult.QueryResult<T>) => void, opts?: QueryResult.SubscriptionOptions): CleanupFn {
    invariant(!(!callback && opts?.fire), 'Cannot fire without a callback.');

    log('subscribe', { query: Query.pretty(this._query), active: this._isActive });
    this._subscribers++;
    const unsubscribeFromEvent = callback ? this._event.on(callback) : undefined;
    this._handleQueryLifecycle();

    // Idempotent: results are cached and shared per query, so a caller releasing twice would
    // otherwise stop the query under every other subscriber and leave the count negative.
    let subscribed = true;
    const unsubscribe = () => {
      if (!subscribed) {
        log.warn('query unsubscribed twice', { query: Query.pretty(this._query) });
        return;
      }
      subscribed = false;
      log('unsubscribe', { query: Query.pretty(this._query), active: this._isActive });
      this._subscribers--;
      unsubscribeFromEvent?.();
      this._handleQueryLifecycle();
    };

    // Fire the initial event synchronously when there is something true to report: every source has
    // answered, this (cached/reused) result was already computed during a prior subscription, or the
    // working set already holds matches. An empty snapshot from a query the index has not answered
    // yet is deferred: a subscriber that creates a default object when it sees none would otherwise
    // act on an answer that was never given.
    if (
      callback &&
      opts?.fire &&
      (!this._queryContext.hasPendingSources() ||
        this._objectCache !== undefined ||
        (this._queryContext.isSynchronous() && this._queryContext.getResults().length > 0))
    ) {
      try {
        callback(this);
      } catch (err) {
        unsubscribe();
        throw err;
      }
    }

    return unsubscribe;
  }

  get completeness(): QueryResult.Completeness {
    return this._queryContext.getCompleteness();
  }

  get sources(): readonly QueryResult.SourceState[] {
    return this._queryContext.getSourceStates();
  }

  get atom(): Atom.Atom<T[]> {
    if (!this._atom) {
      this._atom = Atom.make((get) => {
        const unsubscribe = this.subscribe(() => {
          get.setSelf(this.runSync());
        });
        get.addFinalizer(unsubscribe);
        return this.runSync();
      });
    }
    return this._atom;
  }

  private async _runOnce(opts?: { timeout?: number }): Promise<PresentedResults<T>> {
    const begin = performance.now();
    const filteredResults = await this._queryContext.run(Context.default(), this._query.ast, {
      timeout: opts?.timeout ?? 30_000,
    });
    const presented = this._presentResults(filteredResults);
    countWork('echo.queryRuns');
    countWork('echo.queryResultObjects', presented.objects.length);
    queryMetrics.executed(this._metricsKey, performance.now() - begin, presented.objects.length, 'run');
    return presented;
  }

  private _ensureCachePresent(): void {
    if (!this._resultCache) {
      this._recomputeResult();
    }
  }

  /**
   * @returns true if the result cache was updated.
   */
  private _recomputeResult(): boolean {
    // TODO(dmaretskyi): Make results unique too.
    const begin = performance.now();
    const results = this._queryContext.getResults();
    const presented = this._presentResults(results);
    const end = performance.now();
    countWork('echo.queryRecomputes');
    countWork('echo.queryResultObjects', presented.objects.length);
    queryMetrics.updated(this._metricsKey, end - begin, presented.objects.length);
    // Time to answer: the first recompute after start at which no source is still outstanding.
    if (this._startedAt !== undefined && !this._queryContext.hasPendingSources()) {
      queryMetrics.executed(this._metricsKey, end - this._startedAt, presented.objects.length, 'reactive');
      this._startedAt = undefined;
    }

    const changed =
      presented.kind === 'groups'
        ? // Same T-is-erased-Group boundary as `_presentResults` — `_objectCache`/`presented.objects`
          // are really `GroupResult[]` here, just typed as `T[]` at this generic class's surface.
          !_groupsEqual(
            this._objectCache as unknown as GroupResult[] | undefined,
            presented.objects as unknown as GroupResult[],
          )
        : presented.kind === 'records'
          ? !this._resultCache ||
            this._resultCache.length !== presented.entries.length ||
            this._resultCache.some((entry, index) => entry.id !== presented.entries[index].id)
          : !this._objectCache ||
            this._objectCache.length !== presented.objects.length ||
            this._objectCache.some((obj, index) => obj.id !== presented.objects[index].id);

    log('recomputeResult', { changed });

    // An aggregate or change query assembles its records fresh on every recompute, so an unchanged result
    // still yields a new array — and `useQuery` reads `results` as its `useSyncExternalStore` snapshot
    // on every render, which would then see a new reference for identical data. Hold the previous
    // arrays in that case only: on the flat path `changed` compares ids and order alone, so pinning
    // would serve stale entities and stale per-row match metadata.
    if (presented.kind === 'entities' || changed) {
      this._resultCache = presented.entries;
      this._objectCache = presented.objects;
    }
    return changed;
  }

  /**
   * Turns flat, row-level source entries into the query's public result shape. For an `aggregate`
   * query (detected by the internal `SourceEntry.group` annotation, which the query context sets
   * uniformly across all entries or none), assembles flat aggregate records instead of deduped rows.
   */
  private _presentResults(entries: SourceEntry<T>[]): PresentedResults<T> {
    const { kept, removed } = this._collapseDuplicates(entries);
    entries = kept;

    if (entries.length > 0 && entries[0].group !== undefined) {
      const { groups, entries: groupEntries } = _assembleGroups(
        entries,
        _groupAggregatesFromQuery(this._query.ast),
        removed,
      );
      return {
        kind: 'groups',
        objects: _asResultRows<T>(groups),
        entries: groupEntries as unknown as QueryResult.EntityEntry<T>[],
      };
    }

    if (entries.length > 0 && entries[0].record !== undefined) {
      return { kind: 'records', objects: _asResultRows<T>(entries.map((entry) => entry.record)), entries };
    }

    return { kind: 'entities', objects: this._uniqueObjects(entries), entries };
  }

  /**
   * Drop entities whose tombstone the index has not caught up with yet — the failure this
   * prevents is code that reads `results.length` or asserts a singleton and breaks on a
   * merged-away duplicate it did not create.
   *
   * Read-only by design: the merge itself runs in the worker off the indexing stream (see
   * `echo-host`'s convergence-key merge), and its tombstones leave query results through ordinary
   * deleted-filtering once re-indexed. This filter only covers the gap in between — cached
   * reactive-query entries hydrated before the re-index. It keys on the deleted flag rather
   * than `mergedInto` (the merge writes both in one change) so that it can never hide a live
   * entity: a loser resurrected by `db.add` stays visible until the worker re-tombstones it,
   * instead of becoming a live-but-unqueryable zombie.
   */
  private _collapseDuplicates(entries: SourceEntry<T>[]): { kept: SourceEntry<T>[]; removed: SourceEntry<T>[] } {
    // A query that explicitly asks for tombstones is asking to see what was merged away, so
    // filtering would defeat it — this is how a diagnostic inspects the losers.
    if (_queryIncludesDeleted(this._query.ast)) {
      return { kept: entries, removed: [] };
    }

    const kept: SourceEntry<T>[] = [];
    const removed: SourceEntry<T>[] = [];
    for (const entry of entries) {
      // The working set already applied the deleted option, so only remote entries can carry a tombstone;
      // skipping the rest keeps this off the per-write path of a synchronous notification.
      const tombstone =
        entry.resolution?.source !== 'local' && isEchoObject(entry.result) && getObjectCore(entry.result).isDeleted();
      (tombstone ? removed : kept).push(entry);
    }
    return { kept, removed };
  }

  private _uniqueObjects(entries: SourceEntry<T>[]): T[] {
    const seen = new Set<unknown>();
    return entries
      .map(({ result }) => result)
      .filter(isNonNullable)
      .filter((object: any) => {
        // Assuming objects have `id` property we can use to dedup.
        if (object.id == null) {
          return true;
        }

        if (seen.has(object.id)) {
          return false;
        }

        seen.add(object.id);
        return true;
      });
  }

  private _handleQueryLifecycle(): void {
    if (this._subscribers === 0 && this._isActive) {
      log('stop query', { query: Query.pretty(this._query) });
      this._stop();
    } else if (this._subscribers > 0 && !this._isActive) {
      log('start query', { query: Query.pretty(this._query) });
      this._start();
    }
  }

  private _start(): void {
    this._isActive = true;
    this._startedAt = performance.now();
    queryMetrics.started(this._metricsKey);
    this._queryContext.start();
    this._diagnostic.isActive = true;
  }

  private _stop(): void {
    this._queryContext.stop();
    this._isActive = false;
    this._diagnostic.isActive = false;
    this._startedAt = undefined;
    queryMetrics.stopped(this._metricsKey);
  }

  private _checkQueryIsRunning(): void {
    if (!this._isActive) {
      throw new Error(
        'Query must have at least 1 subscriber for `.results` and `.entries` to be used. Use query.run() for single-use result retrieval.',
      );
    }
  }
}

/**
 * Runtime shape of a `Query.aggregate` row once its type parameter is erased: a flat record with one
 * field per declared aggregate ({@link Query.aggregate}), including the group-key fields.
 */
type GroupResult = { [field: string]: unknown };

/**
 * Boundary cast: T is a plain record for aggregate and change queries (`Query.RecordResult`), but
 * this class is written generically over the entity type — records are a presentation transform
 * applied on top of row-level entries, with the row type erased at runtime.
 */
const _asResultRows = <T>(rows: readonly unknown[]): T[] => rows as unknown as T[];

/**
 * Buckets flat row-level entries into flat aggregate records, in the order groups first appear in
 * `entries`. The host/local query sources already deliver an aggregate query's entries with groups
 * contiguous and correctly ordered (see `AggregateStep`); this only needs to re-derive that grouping
 * locally, after row objects have deduped/hydrated on the client. Group-key fields and declared
 * aggregates become top-level record fields — group keys spread from the source `key`, others
 * recomputed here from each group's hydrated members (`max`/`min`/`items`) or taken from the source
 * (`count`); the ordering they drive is already applied by the source. A group with unhydrated
 * members reflects only the hydrated subset for member-derived aggregates.
 */
const _assembleGroups = (
  entries: SourceEntry[],
  aggregates: readonly QueryAST.GroupAggregate[],
  removed: SourceEntry[] = [],
): { groups: GroupResult[]; entries: QueryResult.Entry<GroupResult>[] } => {
  const seenIds = new Set<unknown>();
  const order: string[] = [];
  const keys = new Map<string, Record<string, unknown>>();
  const members = new Map<string, unknown[]>();
  const counts = new Map<string, number>();
  const sourceAggregates = new Map<string, Record<string, AggregateValue>>();

  for (const entry of entries) {
    if (!entry.group) {
      continue;
    }

    const objectId = entry.result?.id;
    if (objectId != null) {
      if (seenIds.has(objectId)) {
        continue;
      }
      seenIds.add(objectId);
    }

    const serializedKey = JSON.stringify(entry.group.key);
    if (!members.has(serializedKey)) {
      members.set(serializedKey, []);
      keys.set(serializedKey, entry.group.key);
      counts.set(serializedKey, entry.group.count);
      order.push(serializedKey);
    }
    if (entry.group.aggregates !== undefined) {
      sourceAggregates.set(serializedKey, entry.group.aggregates);
    }
    if (entry.result != null) {
      members.get(serializedKey)!.push(entry.result);
    }
  }

  // Tombstones the collapse dropped are still inside the server-side `count`; subtract them so the
  // count agrees with the members it is reported alongside.
  const removedIds = new Map<string, Set<unknown>>();
  for (const entry of removed) {
    const objectId = entry.result?.id;
    if (!entry.group || objectId == null) {
      continue;
    }
    const serializedKey = JSON.stringify(entry.group.key);
    const count = counts.get(serializedKey);
    const ids = removedIds.get(serializedKey) ?? new Set<unknown>();
    if (count === undefined || ids.has(objectId)) {
      continue;
    }
    ids.add(objectId);
    removedIds.set(serializedKey, ids);
    counts.set(serializedKey, count - 1);
  }

  const groups = order.map((serializedKey): GroupResult => {
    const groupMembers = members.get(serializedKey)!;
    // Group-key fields are already keyed by their result-field name; spread them flat.
    const record: GroupResult = { ...keys.get(serializedKey)! };
    const computed = sourceAggregates.get(serializedKey);
    for (const aggregate of aggregates) {
      if (QueryAST.isGroupKeyAggregate(aggregate)) {
        continue; // Group-key fields come from the spread above.
      }
      // A collapsed group has no members to reduce; the source's value is the only one there is.
      record[aggregate.name] =
        computed !== undefined && aggregate.kind !== 'items'
          ? computed[aggregate.name]
          : _computeAggregate(aggregate, groupMembers, counts.get(serializedKey)!);
    }
    return record;
  });
  const groupEntries = order.map((serializedKey, index) => ({ id: serializedKey, result: groups[index] }));
  return { groups, entries: groupEntries };
};

/** Materialises one aggregate for a group from its hydrated members (or the source count). */
const _computeAggregate = (aggregate: QueryAST.GroupAggregate, members: readonly unknown[], count: number): unknown => {
  switch (aggregate.kind) {
    case 'group':
    case 'type':
    case 'timestamp':
    case 'time':
      return undefined; // Group-key fields are assembled from the source key, not here.
    case 'sum':
      return GroupBy.sum(members.map((value) => getDeep(value as Record<string, unknown>, [aggregate.property])));
    case 'items':
      return aggregate.limit !== undefined ? members.slice(0, aggregate.limit) : members;
    case 'count':
      return count;
    case 'max':
    case 'min':
      return GroupBy.reduceAggregate(
        // Row objects are erased to `unknown` at this presentation boundary (see the boundary cast in
        // `_presentResults`); the aggregate reads a dynamic property path off the hydrated object.
        members.map((value) => _coerceScalar(getDeep(value as Record<string, unknown>, [aggregate.property]))),
        aggregate.kind,
      );
  }
};

/** Coerces a raw property value to the scalar aggregate domain (non-scalars → `null`). */
const _coerceScalar = (value: unknown): AggregateValue =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? value : null;

/**
 * Extracts the aggregate clause's declarations from a query AST, unwrapping the wrappers permitted
 * above an `aggregate` (`order`/`limit`/`skip`/`from`/`options`). Empty when the query has no
 * `aggregate` clause.
 */
const _groupAggregatesFromQuery = (query: QueryAST.Query): readonly QueryAST.GroupAggregate[] => {
  let node: QueryAST.Query | undefined = query;
  while (node && node.type !== 'aggregate' && 'query' in node) {
    node = node.query;
  }
  return node?.type === 'aggregate' ? node.aggregates : [];
};

/**
 * Compares two flat aggregate result sets field-by-field — mirrors the row-level id diff used for
 * non-aggregate queries. Member arrays (the `items` aggregate) compare by id sequence; scalar fields
 * (group keys and `max`/`min`/`count`) compare by value.
 */
const _groupsEqual = (prev: GroupResult[] | undefined, next: GroupResult[]): boolean => {
  if (!prev || prev.length !== next.length) {
    return false;
  }
  return prev.every((prevGroup, index) => {
    const nextGroup = next[index];
    const fields = Object.keys(prevGroup);
    if (fields.length !== Object.keys(nextGroup).length) {
      return false;
    }
    return fields.every((field) => {
      const prevValue = prevGroup[field];
      const nextValue = nextGroup[field];
      if (Array.isArray(prevValue) && Array.isArray(nextValue)) {
        return (
          prevValue.length === nextValue.length &&
          // Member row objects are Entities with an `id`; compare identity by id sequence.
          prevValue.every(
            (value, valueIndex) => (value as { id: unknown })?.id === (nextValue[valueIndex] as { id: unknown })?.id,
          )
        );
      }
      return prevValue === nextValue;
    });
  });
};

// NOTE: Make sure this doesn't keep references to the queries so that they can be garbage collected.
type QueryDiagnostic = {
  isActive: boolean;
  filter: string;
  creationStack: StackTrace;
};

/**
 * Live queries, for the `client-queries` diagnostic.
 *
 * Held weakly, and this is load-bearing rather than tidy. A diagnostic record is reachable only from
 * its own `QueryResultImpl`, but it carries an unformatted `creationStack`, and an unformatted stack
 * retains the receiver of every frame it captured — including the `QueryResultImpl` under
 * construction, hence its query context, hypergraph, client, database, and every document loaded
 * through them. A strong entry here therefore pinned one whole client graph per query for the
 * lifetime of the process, which killed long-lived hosts: a Cloudflare Worker running ECHO
 * operations on a cron trigger OOMed after ~400 invocations (DX-1140). Weak refs let the query, its
 * diagnostic, and that graph collect together; the finalizer then drops the empty ref.
 */
const QUERIES = new Set<WeakRef<QueryDiagnostic>>();

const QUERY_FINALIZATION = new FinalizationRegistry<WeakRef<QueryDiagnostic>>((ref) => {
  QUERIES.delete(ref);
});

const registerQueryDiagnostic = (diagnostic: QueryDiagnostic): void => {
  const ref = new WeakRef(diagnostic);
  QUERIES.add(ref);
  QUERY_FINALIZATION.register(diagnostic, ref);
};

trace.diagnostic({
  id: 'client-queries',
  name: 'Queries (Client)',
  fetch: () => {
    return Array.from(QUERIES, (ref) => ref.deref())
      .filter(isNonNullable)
      .map((query) => {
        return {
          isActive: query.isActive,
          filter: query.filter,
          creationStack: query.creationStack.getStack(),
        };
      });
  },
});
